import { useId } from 'react';

import type {
  Business,
  OpenFilter,
  ResultFilter,
  SearchResponse,
  SortKey,
  WebsiteQuality,
} from '../../types';
import type { BatchProgress } from '../App';
import { CupMark, IconDownload, IconRefresh } from '../components/Icons';
import { ResultCard, type RowActions } from '../components/ResultCard';
import { ToTreatFolder } from '../components/ToTreatFolder';
import { TreatedFolder } from '../components/TreatedFolder';

interface ResultsScreenProps extends RowActions {
  result: SearchResponse;
  mainList: readonly Business[];
  toTreat: readonly Business[];
  treated: readonly Business[];
  counts: Record<ResultFilter, number>;
  openCounts: Record<OpenFilter, number>;
  filter: ResultFilter;
  onFilterChange: (filter: ResultFilter) => void;
  openFilter: OpenFilter;
  onOpenFilterChange: (filter: OpenFilter) => void;
  localSearch: string;
  onLocalSearchChange: (value: string) => void;
  sort: SortKey;
  onSortChange: (sort: SortKey) => void;
  onQuality: (business: Business, quality: WebsiteQuality) => void;
  onAnalyze: (business: Business) => void;
  onAnalyzeAll: () => void;
  analyzing: ReadonlySet<string>;
  batchProgress: BatchProgress | null;
  onExport: () => void;
  actionError: string | null;
  loadingMore: boolean;
  onLoadMore: () => void;
  onRefreshSearch: () => void;
  /** Revenir au formulaire, paramètres intacts. */
  onEdit: () => void;
  now: Date;
}

const FILTERS: Array<{ value: ResultFilter; label: string }> = [
  { value: 'all', label: 'Tous' },
  { value: 'without-website', label: 'Sans site' },
  { value: 'social-only', label: 'Réseau social' },
  { value: 'platform-only', label: 'Plateforme' },
  { value: 'priority', label: 'Prioritaires' },
  { value: 'with-phone', label: 'Joignables' },
];

const OPEN_FILTERS: Array<{ value: Exclude<OpenFilter, 'all'>; label: string }> = [
  { value: 'open', label: 'Ouverts' },
  { value: 'closed', label: 'Fermés' },
];

export function ResultsScreen(props: ResultsScreenProps) {
  const searchId = useId();
  const sortId = useId();
  const { result, mainList } = props;

  const what =
    result.search.mode === 'multi-category' ? 'Toutes catégories' : result.search.query;
  const where = `${result.search.radiusKm} km autour de ${result.search.formattedAddress}`;

  return (
    <div className="stack">
      <div className="page-head">
        <div style={{ minWidth: 0 }}>
          {/* Le titre annonce le LOT, pas la seule liste principale. Afficher
              « 1 à prospecter » quand la recherche en a rendu 5 donnait l'air
              d'un échec : le dossier « À traiter » contenait les 4 autres. */}
          <h1 className="title title-lg">
            {result.count} {result.count > 1 ? 'entreprises' : 'entreprise'}
          </h1>
          <p className="page-sub">
            {what} · {where}
          </p>
        </div>
        <button type="button" className="chip" onClick={props.onEdit}>
          Modifier
        </button>
      </div>

      {result.count === 0 ? (
        <div className="empty">
          <CupMark />
          <p className="empty-title">Aucun prospect dans ce périmètre</p>
          <p className="empty-text">
            Élargissez le rayon, ou revenez à « toutes catégories » pour voir ce qu’il y a.
          </p>
          <button type="button" className="btn btn-go" style={{ marginTop: 18 }} onClick={props.onEdit}>
            Modifier la recherche
          </button>
        </div>
      ) : (
        <>
          <p className="page-sub" style={{ marginTop: -6 }}>
            {mainList.length} à prospecter
            {props.toTreat.length > 0
              ? ` · ${props.toTreat.length} ${props.toTreat.length > 1 ? 'ont' : 'a'} déjà un site`
              : ''}
          </p>

          {props.actionError !== null ? (
            <p className="notice notice-error" role="alert">
              {props.actionError}
            </p>
          ) : null}

          {result.search.fromCache ? (
            <p className="notice notice-info">
              Résultats en cache — aucun appel Google, donc rien de facturé.
              <button type="button" className="link" onClick={props.onRefreshSearch}>
                Actualiser
              </button>
            </p>
          ) : null}

          {/* Google est interrogé sur un RECTANGLE englobant le cercle : ses
              coins portent à 1,41 fois le rayon. Le serveur retaille ensuite au
              cercle demandé. Quand une ville dense borde la zone, Google la
              privilégie et l'écart devient énorme — il faut le dire, sinon
              l'utilisateur croit que la recherche a échoué.

              Replié : le constat tient sur une ligne, l'explication est là pour
              qui se pose la question. Déplié en permanence, ce paragraphe
              poussait le premier résultat sous la ligne de flottaison à chaque
              recherche — on faisait payer à toutes les consultations le prix
              d'une question qu'on ne se pose qu'une fois. */}
          {result.search.rawResultCount > result.count ? (
            <details className="notice fold">
              <summary>
                <strong>
                  {result.count} sur {result.search.rawResultCount}
                </strong>{' '}
                rapportés par Google sont dans les {result.search.radiusKm} km
                <span className="fold-more">Pourquoi ?</span>
              </summary>
              <p className="fold-body">
                Les autres sont hors rayon : Google cherche dans un carré englobant, plus large
                que le cercle, et privilégie les établissements les plus connus — souvent ceux
                du centre-ville voisin.
                {result.search.hasMore
                  ? ' Chargez une page de plus pour en trouver d’autres dans la zone.'
                  : ''}
              </p>
            </details>
          ) : null}

          {/* Espaces insécables dans les guillemets : sans elles, un retour à la
              ligne laisse un « » » orphelin en début de ligne. */}
          {result.search.ignoredCount > 0 ? (
            <p className="notice">
              {result.search.ignoredCount} établissement
              {result.search.ignoredCount > 1 ? 's' : ''} ignoré
              {result.search.ignoredCount > 1 ? 's' : ''} masqué
              {result.search.ignoredCount > 1 ? 's' : ''}. Cochez «&nbsp;Afficher les
              ignorés&nbsp;» dans la recherche pour les revoir.
            </p>
          ) : null}

          {/* --- Filtrer et trier ------------------------------------------ */}
          <div className="chips-scroll" role="group" aria-label="Filtrer les résultats">
            {FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                className="chip"
                aria-pressed={props.filter === option.value}
                onClick={() => props.onFilterChange(option.value)}
              >
                {option.label}
                <span className="chip-count">{props.counts[option.value]}</span>
              </button>
            ))}
            {OPEN_FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                className="chip"
                aria-pressed={props.openFilter === option.value}
                onClick={() =>
                  props.onOpenFilterChange(
                    props.openFilter === option.value ? 'all' : option.value,
                  )
                }
              >
                {option.label}
                <span className="chip-count">{props.openCounts[option.value]}</span>
              </button>
            ))}
          </div>

          <div className="toolbar-row">
            <div className="field">
              <label className="sr-only" htmlFor={searchId}>
                Filtrer par nom
              </label>
              <input
                id={searchId}
                className="input"
                type="search"
                placeholder="Filtrer par nom…"
                value={props.localSearch}
                onChange={(event) => props.onLocalSearchChange(event.target.value)}
              />
            </div>
            <div className="field">
              <label className="sr-only" htmlFor={sortId}>
                Trier par
              </label>
              <select
                id={sortId}
                className="select"
                value={props.sort}
                onChange={(event) => props.onSortChange(event.target.value as SortKey)}
              >
                <option value="score">Score</option>
                <option value="distance">Distance</option>
                <option value="rating">Note</option>
                <option value="reviews">Avis</option>
                <option value="name">Nom</option>
              </select>
            </div>
          </div>

          {/* --- La liste --------------------------------------------------- */}
          {mainList.length === 0 ? (
            <div className="empty">
              <p className="empty-title">Aucun résultat pour ce filtre</p>
              <p className="empty-text">Retirez un filtre pour élargir la liste.</p>
            </div>
          ) : (
            <ul className="rows">
              {mainList.map((business) => (
                <ResultCard
                  key={business.placeId}
                  business={business}
                  onSave={props.onSave}
                  onIgnore={props.onIgnore}
                  onUnignore={props.onUnignore}
                  onUntreat={props.onUntreat}
                  onCallStart={props.onCallStart}
                  pendingPlaceId={props.pendingPlaceId}
                  now={props.now}
                />
              ))}
            </ul>
          )}

          <ToTreatFolder
            businesses={props.toTreat}
            onSave={props.onSave}
            onIgnore={props.onIgnore}
            onQuality={props.onQuality}
            onAnalyze={props.onAnalyze}
            onAnalyzeAll={props.onAnalyzeAll}
            onCallStart={props.onCallStart}
            analyzing={props.analyzing}
            batchProgress={props.batchProgress}
            pendingPlaceId={props.pendingPlaceId}
          />

          <TreatedFolder
            businesses={props.treated}
            onUntreat={props.onUntreat}
            pendingPlaceId={props.pendingPlaceId}
          />

          {/* Chaque page suivante coûte un appel Google : elle ne part jamais
              toute seule, et son coût est dit avant le clic. */}
          {result.search.hasMore ? (
            <div className="load-more">
              <span className="cost">
                Page {result.search.loadedPages} chargée — 1 requête Google de plus.
              </span>
              <button
                type="button"
                className="btn btn-soft"
                onClick={props.onLoadMore}
                disabled={props.loadingMore}
              >
                <IconRefresh size={15} />
                {props.loadingMore ? 'Chargement…' : '20 résultats de plus'}
              </button>
            </div>
          ) : (
            <p className="list-end">Fin des résultats · {what}</p>
          )}

          <div className="load-more">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={props.onExport}
              disabled={mainList.length === 0}
            >
              <IconDownload size={15} />
              Exporter en CSV
            </button>
          </div>
        </>
      )}
    </div>
  );
}
